import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2, Search, Plus, Eye,
} from "lucide-react";
import { api } from "../../lib/auth";

function StatusBadge({ status }) {
  const m = {
    active: "bg-[#E8F5E9] text-[#1FA868]",
    pending: "bg-[#FFF5D6] text-amber-700",
    rejected: "bg-rose-50 text-rose-600",
    suspended: "bg-slate-100 text-slate-700",
  };
  return (
    <span className={`text-[10px] font-bold uppercase px-2.5 py-1 rounded-md ${m[status] || "bg-black/5 text-black/60"}`}>
      {status}
    </span>
  );
}

export default function AdminTenants() {
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState(params.get("status_filter") || "all");
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (f = filter) => {
    setLoading(true);
    try {
      const r = await api.get("/admin/tenants", { params: { status_filter: f } });
      setTenants(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(filter); setParams({ status_filter: filter }); }, [filter]);

  const filtered = tenants.filter((t) =>
    !search ||
    t.company_name?.toLowerCase().includes(search.toLowerCase()) ||
    t.subdomain?.toLowerCase().includes(search.toLowerCase()) ||
    t.admin_email?.toLowerCase().includes(search.toLowerCase())
  );

  const stats = {
    total: tenants.length,
    active: tenants.filter(t => t.status === "active").length,
    pending: tenants.filter(t => t.status === "pending").length,
  };

  return (
    <div className="p-8 lg:p-10 max-w-[1400px] space-y-6">

      {/* 🔥 Header */}
      <div className="flex justify-between items-end flex-wrap gap-4">
        <div>
          <h1 className="font-display font-extrabold text-4xl text-black">
            Tenants
          </h1>
          <p className="text-black/60 text-sm mt-1">
            Manage all tenant workspaces.
          </p>
        </div>

        <button className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#1B4332] text-white text-sm font-semibold hover:bg-[#133124]">
          <Plus size={14} /> New Tenant
        </button>
      </div>

      {/* 📊 Stats */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-[#EEF6FF] rounded-2xl p-5 border border-black/5">
          <div className="text-xs text-black/50">Total Tenants</div>
          <div className="text-2xl font-bold">{stats.total}</div>
        </div>

        <div className="bg-[#E8F5E9] rounded-2xl p-5 border border-black/5">
          <div className="text-xs text-black/50">Active</div>
          <div className="text-2xl font-bold text-[#1FA868]">{stats.active}</div>
        </div>

        <div className="bg-[#FFF5D6] rounded-2xl p-5 border border-black/5">
          <div className="text-xs text-black/50">Pending</div>
          <div className="text-2xl font-bold">{stats.pending}</div>
        </div>
      </div>

      {/* 🔍 Search + Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 bg-white rounded-xl border border-black/10 px-3 py-2 flex-1 max-w-md">
          <Search size={16} className="text-black/40" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tenants..."
            className="flex-1 bg-transparent outline-none text-sm"
          />
        </div>

        {["all", "active", "pending", "suspended"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3.5 py-2 rounded-lg text-sm font-medium ${
              filter === f
                ? "bg-[#1B4332] text-white"
                : "bg-white border border-black/10 text-black/70"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* 📋 Table */}
      <div className="bg-white rounded-2xl border border-black/8 overflow-hidden shadow-sm">

        <div className="px-6 py-4 border-b border-black/8 flex justify-between">
          <h2 className="font-semibold text-black">All Tenants</h2>
          <span className="text-xs text-black/50">{filtered.length} records</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] text-black/55 text-[11px] uppercase">
              <tr>
                <th className="px-6 py-3 text-left">Company</th>
                <th className="px-6 py-3 text-left">Subdomain</th>
                <th className="px-6 py-3 text-left">Plan</th>
                <th className="px-6 py-3 text-left">Amount</th>
                <th className="px-6 py-3 text-left">Status</th>
                <th className="px-6 py-3 text-left">Created</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-black/5">
              {loading && (
                <tr><td colSpan="7" className="p-8 text-center">Loading...</td></tr>
              )}

              {filtered.map((t) => (
                <tr key={t.id} className="hover:bg-[#F9FAFB]">

                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-[#F4F8F3] flex items-center justify-center font-bold text-xs">
                        {t.company_name?.slice(0, 2)}
                      </div>
                      <div>
                        <div className="font-semibold">{t.company_name}</div>
                        <div className="text-xs text-black/50">{t.admin_email}</div>
                      </div>
                    </div>
                  </td>

                  <td className="px-6 py-4 text-xs font-mono">
                    {t.subdomain}.brivo360.com
                  </td>

                  <td className="px-6 py-4">{t.plan_name}</td>

                  <td className="px-6 py-4 font-semibold">
                    ₦{Number(t.amount || 0).toLocaleString()}
                  </td>

                  <td className="px-6 py-4">
                    <StatusBadge status={t.status} />
                  </td>

                  <td className="px-6 py-4 text-xs text-black/50">
                    {(t.created_at || "").slice(0, 10)}
                  </td>

                  <td className="px-6 py-4 text-right">
                    <Link
                      to={`/admin/tenants/${t.id}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md border border-black/10 hover:border-black/25"
                    >
                      <Eye size={12} /> View
                    </Link>
                  </td>

                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}